/**
 * An `ExportWorkbook` written into a real ExcelJS workbook.
 *
 * Separate from the service for one reason: this is the half that can be
 * wrong without TypeScript noticing. ExcelJS is untyped here (see
 * `src/exceljs.d.ts`), so a misspelt `autoFilter` key or a number format set
 * on the wrong object compiles perfectly and produces a file that opens
 * blank — or does not open. Taking the library as an argument lets
 * `scripts/verify-export.ts` run this exact code against the real ExcelJS and
 * read the cells back out.
 */
import {CellFormat, ExportSheet, ExportWorkbook} from './export-model';

/** Excel number formats, one per `CellFormat`. */
export const NUMBER_FORMATS: { [format in CellFormat]?: string } = {
  date: 'yyyy-mm-dd',
  datetime: 'yyyy-mm-dd hh:mm',
  money: '#,##0',
  percent: '0%',
};

/**
 * An instant, as the wall clock the exporter is reading.
 *
 * ExcelJS turns a `Date` into an Excel serial through UTC, and Excel has no
 * time zone at all — so an approval made at 16:12 in Prague would be written,
 * and read, as 14:12. Shifting by the local offset makes the number in the
 * cell the time somebody would say out loud; the header block names the zone
 * so the file still says what it means.
 *
 * Date-only cells are deliberately left alone: those are already UTC midnight,
 * which is the exact serial for that day, and shifting one would move every
 * broadcast in the file to the day before.
 */
function wallClock(value: Date): Date {
  return new Date(value.getTime() - value.getTimezoneOffset() * 60000);
}

/**
 * `excel` is the ExcelJS namespace — the browser bundle in the app, the Node
 * package in the verification script. Only `Workbook` is used, and the two
 * builds agree on all of it.
 */
export function renderWorkbook(excel: any, spec: ExportWorkbook): any {
  const book = new excel.Workbook();
  book.created = new Date();
  spec.sheets.forEach(sheet => renderSheet(book.addWorksheet(sheet.name), sheet));
  return book;
}

/** One sheet: the header block if it has one, then the table. */
function renderSheet(sheet: any, spec: ExportSheet): void {
  (spec.meta ?? []).forEach(([label, value]) => {
    const row = sheet.addRow([label, value instanceof Date ? wallClock(value) : value]);
    row.getCell(1).font = {bold: true};
    if (value instanceof Date) {
      row.getCell(2).numFmt = NUMBER_FORMATS.datetime;
    }
  });
  if (spec.meta?.length) {
    sheet.addRow([]);
  }

  const headerRow = sheet.addRow(spec.columns.map(column => column.header));
  headerRow.font = {bold: true};

  spec.rows.forEach(row => sheet.addRow(row));

  if (spec.footer) {
    sheet.addRow(spec.footer).font = {bold: true};
  }

  // Widths and formats go on after the rows, not before: `addRow` on a sheet
  // whose columns were touched first is where ExcelJS starts inventing extra
  // columns, and the header block above is only two of them wide anyway.
  spec.columns.forEach((column, index) => {
    const target = sheet.getColumn(index + 1);
    target.width = Math.max(target.width ?? 0, column.width);
    const format = column.format ? NUMBER_FORMATS[column.format] : undefined;
    if (format) {
      // Cell by cell from the header down. Setting `numFmt` on the column
      // would reformat the label/value block above it too, and turn the
      // production's name into a date.
      for (let row = headerRow.number + 1; row <= sheet.rowCount; row++) {
        const cell = sheet.getRow(row).getCell(index + 1);
        cell.numFmt = format;
        if (column.format === 'datetime' && cell.value instanceof Date) {
          cell.value = wallClock(cell.value);
        }
      }
    }
  });

  // Freeze everything above the first data row, so scrolling a season keeps
  // the headers — and on the first sheet the production's name — in sight.
  sheet.views = [{state: 'frozen', ySplit: headerRow.number}];
  sheet.autoFilter = {
    from: {row: headerRow.number, column: 1},
    to: {row: headerRow.number + spec.rows.length, column: spec.columns.length},
  };
}
